
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from './lib/supabase.ts';
import { TIER_CONFIG, DEFAULT_STATS, getAuraData } from './constants.tsx';
import { MemberTier, UserStats, UIImage, AuraNotification, AppView, AuthView, VaultVisibility } from './types.ts';

// Shared Components
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation.tsx';
import KitchenView from './components/KitchenView.tsx';
import VaultView from './components/VaultView.tsx';
import LeaderboardView from './components/LeaderboardView.tsx';
import ProfileView from './components/ProfileView.tsx';
import AccountView from './components/AccountView.tsx';
import StoreView from './components/StoreView.tsx';
import VibeCheckView from './components/VibeCheckView.tsx';
import TopRotView from './components/TopRotView.tsx';
import RarityGuideView from './components/RarityGuideView.tsx';
import AuraGuideView from './components/AuraGuideView.tsx';
import AuthPortal from './components/AuthPortal.tsx';
import MemePreviewModal from './components/MemePreviewModal.tsx';
import InteractiveStarfieldShader from './components/ui/light-up-shader.tsx';
import { trackPageView, trackEvent } from './lib/analytics.ts';

// Launchpad Components
import LaunchpadLayout from './components/launchpad/LaunchpadLayout.tsx';
import LaunchpadLanding from './components/launchpad/LaunchpadLanding.tsx';
import LaunchpadAbout from './components/launchpad/LaunchpadAbout.tsx';
import LaunchpadPolicy from './components/launchpad/LaunchpadPolicy.tsx';
import LaunchpadCoin from './components/launchpad/LaunchpadCoin.tsx';

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeView, setActiveView] = useState<AppView>('editor');
  const [authView, setAuthView] = useState<AuthView>('app');
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [profileName, setProfileName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [vaultVisibility, setVaultVisibility] = useState<VaultVisibility>('public');
  const [dailyUsage, setDailyUsage] = useState(0);
  const [bonusBlasts, setBonusBlasts] = useState(0);
  const [userTier, setUserTier] = useState<MemberTier>('guest');
  const [notifications, setNotifications] = useState<AuraNotification[]>([]);
  const [isBgEnabled, setIsBgEnabled] = useState(true);
  const [stats, setStats] = useState<UserStats>(() => {
    try { const saved = localStorage.getItem('brainrot_stats'); return saved ? JSON.parse(saved) : { ...DEFAULT_STATS }; }
    catch { return { ...DEFAULT_STATS }; }
  });
  const [allHistory, setAllHistory] = useState<UIImage[]>([]);
  const [previewImage, setPreviewImage] = useState<UIImage | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [viewedProfileId, setViewedProfileId] = useState<string | null>(null);

  const notify = useCallback((message: string, type: 'success' | 'error' | 'info' | 'glitch' = 'info') => {
    const id = Math.random().toString(36).substring(7);
    setNotifications(prev => [...prev, { id, message, type }]);
    
    if (type === 'glitch') {
      trackEvent('glitch_unlocked', { message });
    }

    setTimeout(() => { setNotifications(prev => prev.filter(n => n.id !== id)); }, 4000);
  }, []);

  const isGuest = useMemo(() => !user || user.is_anonymous || user.app_metadata?.is_anonymous, [user]);
  const entitlements = useMemo(() => TIER_CONFIG[userTier], [userTier]);
  
  const aura = useMemo(() => getAuraData(stats.xp), [stats.xp]);

  useEffect(() => {
    trackPageView(`/${activeView}`, activeView.toUpperCase());
  }, [activeView]);

  const loadProfileData = useCallback(async (userId: string) => {
    try {
      const { data } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
      if (data) {
        setUserTier(data.tier as MemberTier || 'registered'); 
        setProfileName(data.username || ''); 
        setAvatarUrl(data.avatar_url || null);
        setVaultVisibility(data.vault_visibility as VaultVisibility || 'public');
        setStats(s => ({ ...s, xp: Number(data.xp) })); 
        setBonusBlasts(data.bonus_blasts || 0);

        const today = new Date().toISOString().split('T')[0];
        if (data.last_reset_date !== today) {
          setDailyUsage(0);
          await supabase.from('profiles').update({ usage_count: 0, last_reset_date: today }).eq('id', userId);
        } else {
          setDailyUsage(data.usage_count || 0);
        }
      }
    } catch (e) {
      console.error("Profile load failed", e);
    }
  }, []);

  const incrementDailyUsage = useCallback(async () => {
    if (bonusBlasts > 0) {
      const nextBonus = bonusBlasts - 1;
      setBonusBlasts(nextBonus);
      if (user && !isGuest) {
        await supabase.from('profiles').update({ bonus_blasts: nextBonus }).eq('id', user.id);
      }
    } else {
      const nextUsage = dailyUsage + 1;
      setDailyUsage(nextUsage);
      if (user && !isGuest) {
        await supabase.from('profiles')
          .update({ 
            usage_count: nextUsage, 
            last_reset_date: new Date().toISOString().split('T')[0] 
          })
          .eq('id', user.id);
      } else {
        localStorage.setItem('brainrot_guest_usage', JSON.stringify({
          count: nextUsage,
          date: new Date().toISOString().split('T')[0]
        }));
      }
    }
  }, [dailyUsage, bonusBlasts, user, isGuest]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const paymentStatus = searchParams.get('payment');
    const tier = searchParams.get('tier');

    if (paymentStatus === 'success' && user) {
      window.history.replaceState({}, '', window.location.pathname);
      
      const processPayment = async () => {
        try {
          if (tier === 'refill') {
            const { data: profile } = await supabase.from('profiles').select('bonus_blasts').eq('id', user.id).single();
            const newTotal = (profile?.bonus_blasts || 0) + 10;
            await supabase.from('profiles').update({ bonus_blasts: newTotal }).eq('id', user.id);
            setBonusBlasts(newTotal);
            notify("ENERGY REFILLED! +10 BONUS BLASTS", "success");
          } else if (tier && TIER_CONFIG[tier as MemberTier]) {
            if (!isGuest) {
              await supabase.from('profiles').update({ tier: tier }).eq('id', user.id);
              await loadProfileData(user.id);
            } else {
              setUserTier(tier as MemberTier);
            }
            notify(`RANK ASCENDED TO ${tier.toUpperCase()}!`, "success");
          }
        } catch (err) {
          notify("PAYMENT SYNC FAILED", "error");
        }
      };
      processPayment();
    }
  }, [user, isGuest, notify, loadProfileData]);

  const addAura = useCallback(async (amount: number, skipNotify = false) => {
    const calculatedAmount = Math.round(amount * (1 + (entitlements.xpBonus || 0)));
    
    setStats(prev => {
      const nextXP = Math.max(0, (prev?.xp || 0) + calculatedAmount);
      if (user && !isGuest) {
        supabase.from('profiles').update({ xp: nextXP }).eq('id', user.id).then();
        supabase.from('aura_logs').insert({ 
          user_id: user.id, 
          amount: calculatedAmount,
          reason: amount > 0 ? 'gain' : 'loss' 
        }).then();
      }
      const nextStats = { ...prev, xp: nextXP };
      localStorage.setItem('brainrot_stats', JSON.stringify(nextStats));
      return nextStats;
    });

    if (!skipNotify) {
      if (amount < 0) notify(`AURA REDUCED (${calculatedAmount})`, "error");
      else notify(`+${calculatedAmount} AURA!`, "success");
    }
    return calculatedAmount;
  }, [user, isGuest, entitlements.xpBonus, notify]);

  const fetchUserHistory = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase.from('memes').select('*').eq('user_id', userId).order('timestamp', { ascending: false });
      if (error) throw error;
      setAllHistory(data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) { 
        setUser(session.user); 
        loadProfileData(session.user.id); 
        fetchUserHistory(session.user.id); 
      } else {
        supabase.auth.signInAnonymously().then(({ data: guest }) => { 
          if (guest.user) setUser(guest.user); 
          setIsAppLoading(false);
        });
      }
    });
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) { 
        setUser(session.user); 
        loadProfileData(session.user.id); 
        fetchUserHistory(session.user.id);
        if (event === 'PASSWORD_RECOVERY') setAuthView('reset_password');
        else if (event === 'SIGNED_IN' && authView !== 'reset_password') setAuthView('app');
      } else { 
        setUser(null); 
        setUserTier('guest'); 
        setProfileName('');
        setAvatarUrl(null);
        setAllHistory([]);
        setStats({ ...DEFAULT_STATS });
        localStorage.removeItem('brainrot_stats');
        setVaultVisibility('public');
        setDailyUsage(0);
        setBonusBlasts(0);
      }
      setIsAppLoading(false);
    });
    return () => subscription.unsubscribe();
  }, [loadProfileData, fetchUserHistory, authView]);

  if (isAppLoading) return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-6">
      <div className="animate-bounce"><ZapIcon size={48} className="text-yellow-400" /></div>
      <p className="font-bungee text-2xl text-yellow-400 uppercase tracking-widest">Warming Up...</p>
    </div>
  );

  return (
    <Routes>
      <Route path="/launchpad/*" element={<LaunchpadLayout onBackToApp={() => navigate('/')} />}>
        <Route index element={<LaunchpadLanding />} />
        <Route path="about" element={<LaunchpadAbout />} />
        <Route path="policy" element={<LaunchpadPolicy />} />
        <Route path=":coinId" element={<LaunchpadCoin />} />
      </Route>
      <Route path="*" element={
        <div className="min-h-screen text-white pb-24 transition-all duration-300">
          {isBgEnabled && <InteractiveStarfieldShader isGenerating={isGenerating} />}
          {!isBgEnabled && <div className="fixed inset-0 z-[-1] bg-black" />}
          
          <div className="sticky top-0 z-[100] w-full">
            <Navigation 
              activeView={activeView} 
              setActiveView={setActiveView} 
              setAuthView={setAuthView}
              isGuest={isGuest}
              avatarUrl={avatarUrl}
              isBgEnabled={isBgEnabled}
              setIsBgEnabled={setIsBgEnabled}
              dailyUsage={dailyUsage}
              bonusBlasts={bonusBlasts}
              dailyLimit={entitlements.dailyLimit}
            />
            
            {!isGuest && (
              <div className="w-full bg-black/80 backdrop-blur-md border-b-2 border-zinc-900 px-4 py-2 shadow-2xl animate-in slide-in-from-top-4">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-2 md:gap-8">
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-bungee text-[10px] text-zinc-500 uppercase tracking-widest">Level</span>
                    <span className="font-bungee text-xl text-yellow-400">{aura.level}</span>
                    <span className="font-bungee text-[10px] text-indigo-400 uppercase tracking-tighter bg-indigo-400/10 px-2 py-0.5 rounded border border-indigo-500/20">{aura.title}</span>
                  </div>
                  <div className="flex-1 w-full space-y-1">
                    <div className="flex justify-between items-center px-1">
                      <span className="text-[8px] font-black text-zinc-600 uppercase tracking-widest">Aura Frequency</span>
                      <span className="text-[8px] font-black text-zinc-400 tabular-nums uppercase">{stats.xp.toLocaleString()} / {aura.nextAuraGoal.toLocaleString()} XP</span>
                    </div>
                    <div className="h-2 w-full bg-zinc-950/40 rounded-full overflow-hidden border border-zinc-900 relative">
                      <div className="h-full bg-gradient-to-r from-indigo-600 to-purple-500 transition-all duration-1000 ease-out" style={{ width: `${aura.percent}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={`fixed ${!isGuest ? 'top-32' : 'top-24'} left-1/2 -translate-x-1/2 z-[10000] flex flex-col gap-2 pointer-events-none`}>
            {notifications.map(n => (
              <div key={n.id} className={`p-4 rounded-2xl border-b-4 flex items-center gap-3 font-bungee text-xs uppercase shadow-2xl transition-all animate-in slide-in-from-top-4 ${n.type === 'success' ? 'bg-emerald-950/80 text-emerald-400 border-emerald-500' : n.type === 'error' ? 'bg-red-950/80 text-red-400 border-red-500' : 'bg-zinc-900/80 text-yellow-400 border-yellow-400'}`}>
                {n.message}
              </div>
            ))}
          </div>

          <main className="max-w-7xl mx-auto p-4 md:p-8">
            {activeView === 'editor' && (
              <KitchenView 
                user={user} isGuest={isGuest} dailyUsage={dailyUsage} incrementDailyUsage={incrementDailyUsage} entitlements={entitlements} 
                vaultVisibility={vaultVisibility} isGenerating={isGenerating} setIsGenerating={setIsGenerating} 
                allHistory={allHistory} setAllHistory={setAllHistory} addAura={addAura} notify={notify} setPreviewImage={setPreviewImage} setActiveView={setActiveView}
                bonusBlasts={bonusBlasts} setAuthView={setAuthView}
              />
            )}
            {activeView === 'toprot' && <TopRotView setPreviewImage={setPreviewImage} setActiveView={setActiveView} />}
            {activeView === 'leaderboard' && <LeaderboardView setActiveView={setActiveView} setViewedProfileId={setViewedProfileId} currentUserId={user?.id} />}
            {activeView === 'profile' && viewedProfileId && (
              <ProfileView userId={viewedProfileId} setActiveView={setActiveView} notify={notify} setPreviewImage={setPreviewImage} currentUserId={user?.id} isGuest={isGuest} addAura={addAura} />
            )}
            {activeView === 'history' && (
              <VaultView 
                user={user} entitlements={entitlements} allHistory={allHistory} setAllHistory={setAllHistory} 
                vaultVisibility={vaultVisibility} setVaultVisibility={setVaultVisibility} 
                fetchUserHistory={fetchUserHistory} setPreviewImage={setPreviewImage} notify={notify} setActiveView={setActiveView}
              />
            )}
            {activeView === 'store' && (
              <StoreView 
                user={user} 
                userTier={userTier} 
                setActiveView={setActiveView} 
                setAuthView={setAuthView}
                isGuest={isGuest}
              />
            )}
            {activeView === 'account' && (
              <AccountView user={user} userTier={userTier} profileName={profileName} setProfileName={setProfileName} avatarUrl={avatarUrl} setAvatarUrl={setAvatarUrl} stats={stats} allHistory={allHistory} notify={notify} setActiveView={setActiveView} setViewedProfileId={setViewedProfileId} />
            )}
            {activeView === 'vibecheck' && <VibeCheckView user={user} isGuest={isGuest} addAura={addAura} notify={notify} setActiveView={setActiveView} setAuthView={setAuthView} />}
            {activeView === 'rarityguide' && <RarityGuideView setActiveView={setActiveView} />}
            {activeView === 'auraguide' && <AuraGuideView setActiveView={setActiveView} />}
          </main>

          <MemePreviewModal 
            previewImage={previewImage} 
            setPreviewImage={setPreviewImage} 
            user={user}
            isGuest={isGuest}
            addAura={addAura}
            notify={notify}
            setAuthView={setAuthView}
          />
          {authView !== 'app' && <AuthPortal authView={authView} setAuthView={setAuthView} notify={notify} isUserLoggedIn={!isGuest && authView !== 'reset_password'} />}
        </div>
      } />
    </Routes>
  );
}

const ZapIcon = ({ size, className }: { size: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
);
